<template>
  <button
    class="BaseButton text-none"
    type="button"
    :class="type"
    :disabled="disabled"
    :aria-disabled="ariaDisabled ? 'true' : undefined"
    :name="name"
    :id="id"
    @click="click"
  >
    <i
      v-if="icon"
      :class="icon"
      aria-hidden="true"
    ></i>
    <slot />
  </button>
</template>

<script setup lang="ts">
  import { useFormPagination } from '@/modules/core/composables/formComposable'
  import type ButtonGMTEvent from '@/modules/core/models/ButtonGMTEvent'
  import { useGTMStore } from '@/modules/core/stores/gtmStore'
  import type { PropType } from 'vue'
  import { computed, useSlots } from 'vue'
  import GTMEventType from '../../models/GTMEventType'

  const gtmStore = useGTMStore()
  const slots = useSlots()
  const { hasPreviousPage } = useFormPagination()

  const emit = defineEmits(['click'])

  const props = defineProps({
    type: {
      type: String,
      default: 'primary'
    },
    icon: {
      type: String,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    ariaDisabled: {
      type: Boolean,
      default: false
    },
    emitGTMEvent: {
      type: Object as PropType<Partial<ButtonGMTEvent>>,
      default: () => {}
    },
    name: {
      type: String,
      default: undefined
    },
    id: {
      type: String,
      default: undefined
    }
  })

  const btn_label = computed(() => slots.default?.()?.[0]?.children || '')
  const eventData = computed<Partial<ButtonGMTEvent> | undefined>(() => {
    if (!props.emitGTMEvent?.event) return undefined
    const data = { ...props.emitGTMEvent }
    if (!data.btn_label) {
      data.btn_label = btn_label.value.toString() || undefined
    }
    if (!data.click_id) {
      data.click_id = props.id || undefined
    }
    if (!data.click_name) {
      data.click_name = props.name || undefined
    }

    return data
  })

  function sendNextBtnEvent() {
    gtmStore.sendEvent({
      ...eventData.value,
      tipo_mensaje: eventData.value!.tipo_mensaje,
      tipo_incidencia: eventData.value!.tipo_incidencia,
      tipo_ramo: eventData.value!.tipo_ramo,
      motivo_baja: eventData.value!.motivo_baja
    })
  }

  function sendBackBtnEvent() {
    gtmStore.sendEvent({
      ...eventData.value,
      contenido_faqs: eventData.value!.contenido_faqs ?? undefined,
      categoria_faqs: eventData.value!.categoria_faqs ?? undefined,
      subcategoria1_faqs: eventData.value!.subcategoria1_faqs ?? undefined,
      subcategoria2_faqs: eventData.value!.subcategoria2_faqs ?? undefined
    })
  }

  function click() {
    if (props.ariaDisabled) return
    if (eventData.value) {
      if (eventData.value.event === GTMEventType.NEXT) sendNextBtnEvent()
      else if (eventData.value.event === GTMEventType.BACK) sendBackBtnEvent()
      else {
        if (eventData.value?.event === GTMEventType.IRAHOME) {
          eventData.value.link_click = hasPreviousPage.value
            ? `${import.meta.env.VITE_API_URL_DXP_ROOT}/tarificador`
            : `${import.meta.env.VITE_API_URL_DXP_ROOT}`
        }
        gtmStore.sendEvent(eventData.value)
      }
    }
    emit('click')
  }
</script>

<style lang="scss" scoped>
  .BaseButton {
    display: flex;
    padding: $spacing-22 $spacing-xl;
    margin: 0 auto;
    width: $size-full;
    justify-content: center;
    align-items: center;
    max-width: $size-375;
    gap: $gap-s;
    border-radius: $b-radius-2;
    color: $color-neutral-00;
    background: $color-primary;
    background-position: center;
    transition: background 0.8s;
    font-family: $font-family-primary;
    font-size: $font-size-l;
    font-style: normal;
    font-weight: $font-weight-700;
    line-height: $line-height-100p;

    span {
      font-weight: inherit;
    }

    &:not(:disabled, [aria-disabled='true']) {
      &.primary {
        color: $color-neutral-00;
        background: $color-primary;

        &:hover {
          background: $color-brand-400 radial-gradient(circle, transparent 1%, $color-brand-400 1%)
            center/15000%;
        }

        &:active {
          background-color: $color-brand-400;
          background-size: 100%;
          transition: background 0s;
        }

        i::before {
          color: var(--color-white);
        }
      }

      &.outlined {
        gap: $gap-10;
        border: 1px solid $color-primary;
        color: $color-primary;
        background-color: $color-neutral-00;

        &:hover {
          background: $color-neutral-00
            radial-gradient(circle, transparent 1%, $color-neutral-00 1%) center/15000%;
        }

        &:active {
          background-color: $color-grey-border;
          background-size: 100%;
          transition: background 0s;
        }
      }

      &.transparent {
        padding: $spacing-m $spacing-l;
        gap: $gap-10;
        color: $color-primary;
        background-color: transparent;
        box-shadow: $box-shadow-none;
        font-size: $spacing-l;
        letter-spacing: $letter-spacing-neg-32;

        i {
          font-size: $font-size-xl;
        }
      }

      &.transparent:focus-visible {
        box-shadow: 0 0 0 4px $color-focus;
      }

      &.highlight {
        background: $color-secondary;

        &:hover {
          background: $color-error-light
            radial-gradient(circle, transparent 1%, $color-error-light 1%) center/15000%;
        }

        &:active {
          background-color: $color-tile-300;
          background-size: 100%;
          transition: background 0s;
        }
      }
    }

    &:disabled,
    &[aria-disabled='true'] {
      cursor: initial;
      background-color: $color-grey-200;
      color: $color-neutral-light;
      border-color: $color-grey-200;

      /* stylelint-disable-next-line no-descending-specificity */
      &.transparent {
        color: $color-brand-200;
        background-color: transparent;
      }
    }
  }
</style>
